import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./LeafletMap.css";

// Approximate ward-level locations only.
// Resident-submitted GPS coordinates are deliberately never displayed publicly.
const WARD_CENTROIDS = [
  { key: "Chandra Layout", match: /chandra/i, coords: [12.9700, 77.5350] },
  { key: "Attiguppe", match: /attigupp/i, coords: [12.9550, 77.5400] },
  { key: "Deepanjali Nagar", match: /deepanjali|dipanjali/i, coords: [12.9490, 77.5305] },
  { key: "Bapujinagar", match: /bapuji/i, coords: [12.9605, 77.5262] },
  { key: "RPC Layout", match: /rpc/i, coords: [12.9660, 77.5455] },
  { key: "Kadamba Nagar", match: /kadamba/i, coords: [12.9755, 77.5410] },
  { key: "Vijayanagar West", match: /vijayanagar\s*west|west vijay/i, coords: [12.9610, 77.5320] },
  { key: "Hosahalli", match: /hosahalli/i, coords: [12.9680, 77.5510] },
  { key: "Gali Anjaneya", match: /gali|anjaneya/i, coords: [12.9450, 77.5450] },
];

const CENTER = [12.9620, 77.5390];

function locateWard(name) {
  if (!name) return CENTER;

  for (const ward of WARD_CENTROIDS) {
    if (ward.match.test(name)) return ward.coords;
  }

  let hash = 0;

  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) & 0xffff;
  }

  const dLat = ((hash % 100) - 50) / 3500;
  const dLng = (((hash >> 8) % 100) - 50) / 3500;

  return [CENTER[0] + dLat, CENTER[1] + dLng];
}

function heat(count, max) {
  if (!count) {
    return {
      color: "#8d857c",
      fill: "#e6ddc9",
    };
  }

  const ratio = count / max;

  if (ratio > 0.66) {
    return {
      color: "#7f4229",
      fill: "#9d543b",
    };
  }

  if (ratio > 0.33) {
    return {
      color: "#b47a24",
      fill: "#d79632",
    };
  }

  return {
    color: "#e0bf7c",
    fill: "#f5d99f",
  };
}

export default function LeafletMap({ complaints = [] }) {
  const wardCounts = useMemo(() => {
    const counts = new Map();

    for (const complaint of complaints || []) {
      const ward = String(complaint?.ward || "").trim();

      if (!ward) continue;

      counts.set(
        ward,
        (counts.get(ward) || 0) + 1
      );
    }

    return Array.from(counts.entries()).map(([ward, count]) => ({
      ward,
      count,
    }));
  }, [complaints]);

  const max = Math.max(
    1,
    ...wardCounts.map((item) => item.count)
  );

  const markers = useMemo(
    () =>
      wardCounts.map((item, index) => {
        const pos = locateWard(item.ward);
        const h = heat(item.count, max);

        const radius =
          12 + Math.min(20, item.count * 4);

        return {
          id: `${item.ward}-${index}`,
          ward: item.ward,
          count: item.count,
          pos,
          h,
          radius,
        };
      }),
    [wardCounts, max]
  );

  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;

    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
      iconUrl:
        "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
      shadowUrl:
        "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    });
  }, []);

  const empty = markers.length === 0;

  return (
    <div
      className="leaflet-wrap"
      data-testid="leaflet-map-wrap"
    >
      <MapContainer
        center={CENTER}
        zoom={14}
        scrollWheelZoom={false}
        className="leaflet-canvas"
        data-testid="leaflet-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {markers.map((marker) => (
          <CircleMarker
            key={marker.id}
            center={marker.pos}
            radius={marker.radius}
            pathOptions={{
              color: marker.h.color,
              fillColor: marker.h.fill,
              fillOpacity: 0.72,
              weight: 2,
            }}
          >
            <Tooltip
              direction="top"
              offset={[0, -6]}
              opacity={1}
            >
              <b>{marker.ward}</b>
              {" — "}
              {marker.count} issue
              {marker.count === 1 ? "" : "s"}
            </Tooltip>

            <Popup>
              <div
                className="leaflet-popup-card"
                data-testid={`leaflet-popup-${marker.id}`}
              >
                <p className="kicker">CIVIC ISSUES</p>
                <h4>{marker.ward}</h4>
                <p>
                  <b>{marker.count}</b>{" "}
                  reported issue
                  {marker.count === 1 ? "" : "s"}
                </p>
                <small>
                  Map location is an approximate ward-level
                  location. Individual complaint locations are
                  not displayed publicly.
                </small>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>

      <div
        className="leaflet-legend"
        data-testid="leaflet-legend"
      >
        <span>Issue density by ward</span>

        <div className="legend-scale">
          <em style={{ background: "#e6ddc9" }} />
          <em style={{ background: "#f5d99f" }} />
          <em style={{ background: "#d79632" }} />
          <em style={{ background: "#9d543b" }} />
          <small>low → high</small>
        </div>

        {empty && (
          <b
            className="empty-hint"
            data-testid="leaflet-empty"
          >
            No civic issues with ward information yet.
          </b>
        )}
      </div>
    </div>
  );
}
